// Verificación de direcciones ANTES de mandarlas a Cabify.
//
// Por qué existe (caso #4650, 07/10/2026): Cabify geocodifica el TEXTO de la dirección por su
// cuenta y, si no la entiende, devuelve un punto de relleno sin avisar. "Gral Roca 2146, 9,
// Buenos Aires, CP 1602" (Florida) terminó en Antofagasta 313, Avellaneda — 16 km — y el
// paquete volvió con WRONG_ADDRESS.
//
// Regla: a Cabify SIEMPRE se le mandan coordenadas (dropoff_loc), nunca solo texto. Las
// sacamos de Google Places API (New) — la misma que usa el autocompletado (AddressField) — y
// las VALIDAMOS contra la orden:
//   1. Google tiene que haber encontrado la altura exacta (street_number), no solo la zona.
//   2. El CÓDIGO POSTAL de la orden tiene que coincidir con el de Google. Es el control que
//      atrapa el caso típico: "Av. Alem 180, Buenos Aires, CP 8000" (Bahía Blanca) Google la
//      resuelve en CABA (C1038) — sin este chequeo saldría a otra ciudad.
//   3. Si la orden tiene altura, tiene que coincidir con la de Google.
// Si algo no cuadra → NO se despacha: se muestra lo que encontró Google y se corrige la
// dirección eligiendo una sugerencia del autocompletado (que trae coordenadas exactas).

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';

export interface AddressCheck {
  ok: boolean;
  lat?: number;
  lon?: number;
  /** Cómo la entendió Google (para mostrar y comparar). */
  found?: string;
  /** Por qué no se puede usar (si ok=false). */
  reason?: string;
}

// Altura (número de calle) de una dirección "Calle 1234 dpto 2, Localidad, ...". Del primer tramo
// se descarta lo que viene después de depto/piso/torre; la altura es el PRIMER número después
// del nombre — salvo calles numeradas ("39 1023", "Calle 12 nro 345", "Av. 7 1234"), donde es
// el segundo. Probado con: Arevalo 2950 dpto 2 → 2950 · Maipu 1521 1D (2000) → 1521 ·
// Santa Fe 1530 7 D → 1530 · 39 1023 → 1023 · Caseros 1751 SN → 1751 · sin número → null.
export function alturaDe(text: string): string | null {
  const seg = (text.split(',')[0] || '').replace(/\b(dpto|depto|dto|departamento|piso|pb|torre|of(icina)?|uf|lote|mza|manzana)\b.*$/i, '');
  const nums = seg.match(/\d{1,5}/g) || [];
  if (!nums.length) return null;
  const numerada = /^\s*((calle|av(enida)?\.?|ruta|diagonal|diag\.?)\s+)?\d+\b/i.test(seg);
  return (numerada ? nums[1] : nums[0]) ?? null;
}

const digits4 = (s: string | null | undefined) => (String(s || '').match(/\d{4}/) || [null])[0];

// ── Búsqueda limpia (15/10/2026) ──
// Antes se buscaba el texto entero de la orden y el piso/depto o las notas ("Piso 3 Depto B",
// "dejar en garita", "Instituto Alexander Fleming") hacían que Google devolviera OTRA calle
// (Guevara 1483 → Caldas 1598). Ahora se prueba primero "calle altura, localidad" y después el
// texto original; gana la primera que pase las validaciones.
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
const STOP = new Set(['av', 'avenida', 'calle', 'gral', 'general', 'dr', 'doctor', 'de', 'del', 'la', 'las', 'los', 'el', 'pje', 'pasaje', 'ing', 'presidente', 'pres', 'santa', 'san']);
const streetWords = (s: string) => norm(s).split(' ').filter(w => w.length > 2 && !/^\d+$/.test(w) && !STOP.has(w));
// segmento que NO ayuda a ubicar: piso/depto/unidad o una nota para el repartidor
const isNoise = (seg: string) => {
  const t = norm(seg);
  if (!t) return true;
  if (/^(cp )?[a-z]?\d{4}[a-z]{0,3}$/.test(t)) return false; // CP: sirve
  if (t.length <= 12 && /^(piso|dpto|depto|dept|apto|dto|unidad|uf|of|oficina|local|timbre|casa|ph|pb|torre|lote|mza)?\s*[\d]{0,4}\s*[a-z0-9]{0,3}$/.test(t)) return true; // "8B", "4", "901", "Piso 9"
  return /\b(piso|dpto|depto|departamento|timbre|dejar|recepcion|garita|porteria|portero|seguridad|esquina|ligustrina|instituto|colegio|escuela|modulo|oficina|edificio|torre|entre|frente|al lado|casa)\b/.test(t);
};
// zona del CP: CABA (1000-1499) es una sola zona; fuera de CABA, ±15 (localidades vecinas)
const sameCpZone = (a: string, b: string) => {
  const x = Number(a), y = Number(b);
  if (x >= 1000 && x <= 1499 && y >= 1000 && y <= 1499) return true;
  return Math.abs(x - y) <= 15;
};

async function searchPlace(q: string): Promise<any | { error: string }> {
  try {
    const r = await fetch('https://places.googleapis.com/v1/places:searchText', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': MAPS_KEY,
        'X-Goog-FieldMask': 'places.formattedAddress,places.location,places.addressComponents,places.types',
      },
      body: JSON.stringify({ textQuery: /argentina/i.test(q) ? q : `${q}, Argentina`, regionCode: 'ar', languageCode: 'es' }),
    });
    const d = await r.json();
    if (!r.ok) return { error: `Google no respondió (${d?.error?.message || r.status})` };
    return d?.places?.[0] || null;
  } catch (e) {
    return { error: 'No se pudo consultar Google: ' + String((e as Error).message || e) };
  }
}

export async function verifyAddress(addr: string): Promise<AddressCheck> {
  const text = (addr || '').trim();
  if (!text) return { ok: false, reason: 'La orden no tiene dirección' };
  if (!MAPS_KEY) return { ok: false, reason: 'Falta la key de Google Maps en el admin' };

  const segs = text.split(',').map(x => x.trim()).filter(Boolean);
  const head = (segs[0] || '').replace(/\b(dpto|depto|dto|departamento|piso|pb|torre|of(icina)?|uf)\b.*$/i, '').trim();
  const numOrden = alturaDe(text);
  // Sin altura en la orden NO se valida: Google completa una cualquiera y el punto parecería verificado.
  if (!numOrden) return { ok: false, reason: 'La dirección de la orden no tiene altura (número de calle)' };
  const cpOrden = digits4((text.match(/\bCP\s*[A-Z]?\s*(\d{4})/i) || [])[1]);
  const isCaba = /capital federal|\bcaba\b|ciudad aut[oó]noma/i.test(text);
  const places = segs.slice(1).filter(x => !isNoise(x) && !/^cp\b/i.test(x.trim()));
  const locality = isCaba ? 'Ciudad Autónoma de Buenos Aires' : places.slice(-2).join(', ');
  const numberedStreet = (head.match(/^\s*(?:(?:calle|av(?:enida)?\.?|diagonal|diag\.?)\s+)?(\d{1,3})\s+\d+/i) || [])[1] || null;
  const queries = Array.from(new Set([
    ...(numberedStreet ? [[`Calle ${numberedStreet} ${numOrden}`, locality].filter(Boolean).join(', ')] : []),
    [head, locality, cpOrden ? `CP ${cpOrden}` : ''].filter(Boolean).join(', '),
    [head, locality].filter(Boolean).join(', '),
    text,
  ]));
  const wanted = streetWords(head.replace(/\d+/g, ' '));

  let firstFail: AddressCheck | null = null;
  for (const q of queries) {
    const p = await searchPlace(q);
    if (p && 'error' in p) return { ok: false, reason: p.error };
    if (!p?.location) { firstFail = firstFail || { ok: false, reason: 'Google no encontró esta dirección' }; continue; }
    const comp = (type: string) => (p.addressComponents || []).find((c: any) => (c.types || []).includes(type))?.longText || null;
    const found = p.formattedAddress as string;
    const lat = Number(p.location.latitude), lon = Number(p.location.longitude);
    const fail = (reason: string) => { firstFail = firstFail || { ok: false, found, lat, lon, reason }; };

    // 1) precisión: tiene que haber altura
    const gNum = String(comp('street_number') || '').replace(/\D/g, '');
    if (!gNum) { fail('Google solo encontró la zona, no la dirección exacta (falta calle o altura)'); continue; }
    // 2) la CALLE tiene que ser la misma (si no, Google eligió otra cosa: Guevara → Caldas)
    const route = streetWords(comp('route') || '');
    if (wanted.length && route.length && !wanted.some(w => route.some(r => r.startsWith(w.slice(0, 5)) || w.startsWith(r.slice(0, 5))))) {
      fail(`Google encontró otra calle: ${comp('route')} (la orden dice "${head}")`); continue;
    }
    // calles NUMERADAS (La Plata: "39 1023" = calle 39 al 1023): el número de la calle tiene que
    // coincidir (antes "39 1023" se aceptaba como "Calle 46 1023")
    if (numberedStreet && (String(comp('route') || '').match(/\d+/) || [''])[0] !== numberedStreet) {
      fail(`Google encontró otra calle: ${comp('route') || '—'} (la orden dice calle ${numberedStreet})`); continue;
    }
    // 3) código postal: solo frena si es OTRA zona (otra ciudad); el CP de 4 dígitos es aproximado
    const cpGoogle = digits4(comp('postal_code'));
    if (cpOrden && cpGoogle && !sameCpZone(cpOrden, cpGoogle)) {
      const zona = comp('locality') || comp('administrative_area_level_2') || '';
      fail(`El código postal no coincide: la orden dice ${cpOrden} y Google la ubicó en ${cpGoogle}${zona ? ` (${zona})` : ''}`); continue;
    }
    // 4) altura: misma calle, hasta 50 números de diferencia (Google interpola: Cerviño 4476 → 4470)
    if (Math.abs(Number(gNum) - Number(numOrden)) > 50) {
      fail(`La altura no coincide: la orden dice ${numOrden} y Google encontró ${gNum}`); continue;
    }
    return { ok: true, lat, lon, found };
  }
  return firstFail || { ok: false, reason: 'Google no encontró esta dirección' };
}

// Para una ORDEN: si ya tiene coordenadas (elegidas en el autocompletado o verificadas antes)
// se usan tal cual; si no, se verifica la dirección y, si pasa, se guardan en la orden para
// que el envío (y cualquier reintento) use siempre ese punto.
export async function ensureOrderCoords(order: {
  id: number; delivery_address?: string | null; delivery_lat?: number | null; delivery_lon?: number | null;
}): Promise<AddressCheck> {
  if (order.delivery_lat != null && order.delivery_lon != null) {
    return { ok: true, lat: Number(order.delivery_lat), lon: Number(order.delivery_lon) };
  }
  const chk = await verifyAddress(order.delivery_address || '');
  if (chk.ok) {
    const { supabase } = await import('./supabase');
    await supabase.from('orders').update({ delivery_lat: chk.lat, delivery_lon: chk.lon }).eq('id', order.id);
  }
  return chk;
}
